import { Controller } from "@hotwired/stimulus"

// Drag-and-drop document upload with a per-file type picker.
//
// Files dropped or chosen are staged in a list; each row gets a type <select> that we
// pre-guess from the filename (the underwriter adjusts as needed). On submit we load the
// staged files into a hidden "carrier" file input via DataTransfer, in the SAME order as
// the rendered document[document_types][] selects, so the server zips file[i] ↔ type[i].
export default class extends Controller {
  static targets = ["dropzone", "picker", "carrier", "list", "rowTemplate", "submit", "count"]

  connect() {
    this.files = []
    this.render()
  }

  browse() { this.pickerTarget.click() }

  picked() {
    this.add(this.pickerTarget.files)
    this.pickerTarget.value = "" // let the same file be re-picked later
  }

  over(e) { e.preventDefault(); this.dropzoneTarget.classList.add("border-primary", "bg-primary/5") }
  leave(e) { e.preventDefault(); this.dropzoneTarget.classList.remove("border-primary", "bg-primary/5") }

  drop(e) {
    e.preventDefault()
    this.leave(e)
    this.add(e.dataTransfer.files)
  }

  add(fileList) {
    Array.from(fileList).forEach((f) => this.files.push(f))
    this.render()
  }

  remove(e) {
    this.files.splice(Number(e.params.index), 1)
    this.render()
  }

  // Copy the staged files into the submitted (carrier) input, in list order. Kept in
  // sync on every render so the carrier is already correct when the form submits — Turbo
  // reads the FormData in the capture phase, BEFORE a submit-time action would run, so we
  // can't populate it lazily on submit.
  sync() {
    const dt = new DataTransfer()
    this.files.forEach((f) => dt.items.add(f))
    this.carrierTarget.files = dt.files
  }

  render() {
    this.listTarget.innerHTML = ""
    this.files.forEach((file, i) => {
      const row = this.rowTemplateTarget.content.firstElementChild.cloneNode(true)
      row.querySelector("[data-name]").textContent = file.name
      row.querySelector("[data-size]").textContent = this.humanSize(file.size)
      row.querySelector("select").value = this.guessType(file.name)
      row.querySelector("[data-remove]").dataset.docUploadIndexParam = i
      this.listTarget.appendChild(row)
    })
    this.sync()
    this.submitTarget.disabled = this.files.length === 0
    if (this.hasCountTarget) {
      this.countTarget.textContent =
        this.files.length ? `${this.files.length} file${this.files.length === 1 ? "" : "s"} ready` : ""
    }
  }

  // Best-effort type guess from the filename; the user can override per row.
  guessType(name) {
    const n = name.toLowerCase()
    if (/statement|stmt|\bbank\b/.test(n)) return "bank_statement"
    if (/driver|licen|\bdl\b|\bid\b/.test(n)) return "drivers_license"
    if (/void|cheque|\bcheck\b/.test(n)) return "voided_check"
    if (/applica|agreement|contract|signed/.test(n)) return "signed_application"
    return "other"
  }

  humanSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1048576) return `${Math.round(bytes / 1024)} KB`
    return `${(bytes / 1048576).toFixed(1)} MB`
  }
}
